/* OTP — Shop the look.
   Each slide carries its own variant map, so choosing sizes resolves to a real
   Shopify variant and the look is added with one request, no page change. */

class OtpShopTheLook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    if (!this.track || !this.slides.length) return;

    this.slides.forEach((slide) => this.wireSlide(slide));

    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

    this.track.addEventListener('scroll', () => this.sync(), { passive: true });
    window.addEventListener('resize', () => this.sync());
    this.sync();
  }

  /* ---------- carrossel ---------- */

  get step_() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.slides[0].getBoundingClientRect().width + gap;
  }

  get index() {
    return Math.round(this.track.scrollLeft / this.step_);
  }

  goTo(i) {
    const max = this.slides.length - 1;
    const target = Math.min(Math.max(i, 0), max);
    this.track.scrollTo({ left: target * this.step_, behavior: 'smooth' });
  }

  step(dir) {
    this.goTo(this.index + dir);
  }

  sync() {
    const i = this.index;
    this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
    this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
  }

  /* ---------- tamanhos e carrinho ---------- */

  wireSlide(slide) {
    const form = slide.querySelector('[data-form]');
    if (!form) return;

    let map = [];
    try {
      map = JSON.parse(slide.querySelector('[data-variants]').textContent);
    } catch (e) {
      return;
    }

    const selects = Array.from(slide.querySelectorAll('[data-option]'));
    const idField = slide.querySelector('[data-variant-id]');
    const button = slide.querySelector('[data-buy]');
    const note = slide.querySelector('[data-note]');

    const resolve = () => {
      const chosen = selects.map((s) => s.value);
      const hit = map.find((v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i]));
      idField.value = hit ? hit.id : '';
      button.disabled = !hit;
      if (!hit && selects.length) note.textContent = 'Esta combinação de tamanhos não está disponível.';
      else if (note.dataset.state !== 'done') note.textContent = '';
      return hit;
    };

    selects.forEach((s) => s.addEventListener('change', resolve));
    resolve();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!resolve()) return;

      const original = button.innerHTML;
      button.disabled = true;
      note.dataset.state = '';
      note.textContent = 'A adicionar…';

      try {
        await this.addToCart(Number(idField.value), 1);

        button.classList.add('is-done');
        button.innerHTML = 'ADICIONADO ✓';
        note.dataset.state = 'done';
        note.innerHTML = '<a href="/cart">Ver carrinho</a>';

        setTimeout(() => {
          button.classList.remove('is-done');
          button.innerHTML = original;
          button.disabled = false;
        }, 3200);
      } catch (err) {
        button.disabled = false;
        note.dataset.state = '';
        note.textContent = 'Não foi possível adicionar. ' + (err.message || '');
      }
    });
  }

  /* ---------- carrinho ----------
     Same sequence as the theme's own product form (product-form.js):
       1. announce the add with a CartLinesUpdateEvent that carries a pending
          promise — <cart-drawer-component> and <cart-items-component> wait on it;
       2. POST /cart/add.js asking for the cart sections in the same response;
       3. re-read /cart.json and resolve the promise with the fresh cart + the
          rendered sections. Only then does the theme morph the drawer contents,
          update the header/floating counters and auto-open the drawer.
     Opening the drawer before step 3 is what used to show "O seu carrinho está
     vazio": the drawer was opened against the stale, pre-add markup. */

  get cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  get cartDrawer() {
    return document.querySelector('theme-drawer#cart-drawer');
  }

  async addToCart(variantId, quantity) {
    const routes = (window.Theme && Theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = (routes.cart_url || '/cart') + '.json';

    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(quantity));
    const sectionIds = this.cartSectionIds;
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }
    const CartLinesUpdateEvent = events && events.CartLinesUpdateEvent;

    // 1. announce (before the request, like the theme does)
    let deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity }],
          promise: deferred.promise,
        })
      );
    }

    const readCart = () =>
      fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

    const settle = (cart, detail) => {
      if (!deferred) return;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: Object.assign(
          {
            items: cart.items,
            source: 'otp-shop-the-look',
            sourceId: this.id || 'otp-shop-the-look',
            itemCount: quantity,
          },
          detail
        ),
      });
    };

    let data;
    try {
      // 2. add, asking for the re-rendered cart sections in the same round trip
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      data = await res.json();

      if (!res.ok || data.status) {
        const message = data.description || data.message || 'erro';
        // the theme resolves with didError so listeners settle without opening
        try {
          settle(await readCart(), { didError: true });
        } catch (e) {
          if (deferred) deferred.reject(e);
        }
        throw new Error(message);
      }

      // 3. fresh cart + sections → the theme morphs the drawer and opens it
      const cart = await readCart();
      settle(cart, { didError: false, productId: data.product_id, sections: data.sections });
    } catch (err) {
      if (deferred && !data) deferred.reject(err);
      throw err;
    }

    if (deferred) {
      // let the theme's listeners run first, then make sure the drawer is open
      // even when "abrir carrinho automaticamente" is off in the theme settings
      await deferred.promise.catch(() => {});
      await this.whenDrawerUpdated();
    } else {
      await this.rerenderCartSections();
    }

    const drawer = this.cartDrawer;
    if (drawer && !drawer.isOpen && typeof drawer.open === 'function') drawer.open();

    return data;
  }

  /* wait (briefly) until the drawer no longer shows the empty state */
  whenDrawerUpdated() {
    const drawer = this.cartDrawer;
    if (!drawer) return Promise.resolve();
    return new Promise((done) => {
      const started = Date.now();
      const tick = () => {
        const empty = drawer.querySelector('.cart-drawer--empty, [data-cart-drawer-empty]');
        if (!empty || Date.now() - started > 1500) return done();
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  /* fallback when the events module is unavailable: re-render the cart sections */
  async rerenderCartSections() {
    try {
      const { sectionRenderer } = await import('@theme/section-renderer');
      await Promise.all(
        Array.from(document.querySelectorAll('cart-items-component')).map((el) =>
          sectionRenderer.renderSection(el.dataset.sectionId, {
            cache: false,
            mode: el.dataset.drawer !== undefined ? 'hydration' : 'full',
          })
        )
      );
    } catch (e) {
      /* nothing else to do: the drawer will refresh on its next own update */
    }
  }
}

if (!customElements.get('otp-shop-the-look')) {
  customElements.define('otp-shop-the-look', OtpShopTheLook);
}
